import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { DataManipulationConfig, FilterFieldDefinition } from '../../models';
import { isFilterFieldGroup } from '../../utils';

interface FilterConfigState { [filterInstanceId: string]: DataManipulationConfig }

@Injectable()
export class FilterDefinitionStoreService {
  private static _behaviorSubject = new BehaviorSubject<FilterConfigState>({});

  private get behaviorSubject(): BehaviorSubject<FilterConfigState> {
    return FilterDefinitionStoreService._behaviorSubject;
  }

  getAllFilterInstances(): Observable<FilterConfigState> {
    return this.behaviorSubject.asObservable();
  }

  add(partition: string, definition: DataManipulationConfig): void {
    const currentState = this.behaviorSubject.value;
    this.behaviorSubject.next({ ...currentState, [partition]: definition });
  }

  getFilterFieldDefinition(filterInstanceId: string, fieldId: string): Observable<FilterFieldDefinition<any>> {
    return this.behaviorSubject.pipe(
      map((state) => state[filterInstanceId]),
      filter((filterDef) => !!filterDef),
      map((filterDef) => {
        return filterDef.fields
          .flatMap((fieldOrGroup) => isFilterFieldGroup(fieldOrGroup))
          .find((definition) => definition.fieldId === fieldId);
      }),
      filter((filterFieldDefinition) => !!filterFieldDefinition),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  remove(filterInstanceId: string): void {
    const newState = { ...this.behaviorSubject.value };
    delete newState[filterInstanceId];
    this.behaviorSubject.next(newState);
  }

  get(filterInstanceId: string): Observable<DataManipulationConfig> {
    return this.behaviorSubject.pipe(
      map((state) => state[filterInstanceId]),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
